import React, { useState, useEffect } from 'react';
import { mealAPI } from '../services/api';
import { Meal } from '../types';
import { MEAL_CATEGORIES, FAMILY_MEMBERS } from '../utils/constants';
import { Modal } from './common/Modal';
import { Input } from './common/Input';

const CATEGORY_STYLE: Record<string, { bg: string; border: string; text: string }> = {
  'Repas rapide':      { bg: '#e0f7fa', border: '#0097a7', text: '#006064' },
  'Dîner familial':    { bg: '#fffde7', border: '#f9a825', text: '#e65100' },
  'Occasion spéciale': { bg: '#fce4ec', border: '#e91e63', text: '#880e4f' },
  'Restes':            { bg: '#f1f8e9', border: '#558b2f', text: '#33691e' },
  'Restaurant':        { bg: '#fbe9e7', border: '#e64a19', text: '#bf360c' },
};

const DETAIL_CATEGORIES = new Set(['Restaurant', 'Occasion spéciale']);

const ChevronRight: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
    <path d="M4.5 2.5L7.5 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface MealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  meal?: Meal | null;
  dateString: string;
}

const MealModal: React.FC<MealModalProps> = ({ isOpen, onClose, onSave, meal = null, dateString }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: (MEAL_CATEGORIES[0]?.name || 'Repas rapide') as Meal['category'],
    people: [] as string[],
    extraPeople: 0,
    description: '',
    location: '',
  });
  const [showExtraPeople, setShowExtraPeople] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (meal) {
        const mealPeople = Array.isArray(meal.people) ? meal.people : [];
        const totalPeople = meal.peopleCount || mealPeople.length || 0;
        const extraPeople = Math.max(0, totalPeople - mealPeople.length);
        setFormData({
          name: meal.name,
          category: meal.category,
          people: mealPeople,
          extraPeople,
          description: meal.description || '',
          location: meal.location || '',
        });
        setShowExtraPeople(extraPeople > 0);
      } else {
        setFormData({
          name: '',
          category: (MEAL_CATEGORIES[0]?.name || 'Repas rapide') as Meal['category'],
          people: [],
          extraPeople: 0,
          description: '',
          location: '',
        });
        setShowExtraPeople(false);
      }
      setError(null);
    }
  }, [isOpen, meal]);

  const togglePerson = (personName: string) => {
    setFormData(prev => ({
      ...prev,
      people: prev.people.includes(personName)
        ? prev.people.filter(p => p !== personName)
        : [...prev.people, personName],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const totalPeople = formData.people.length + formData.extraPeople;
      const name = formData.name.trim() || formData.category;
      const mealData = { ...formData, name, peopleCount: totalPeople, dateString };

      if (meal && meal._id) {
        await mealAPI.update(meal._id, mealData);
      } else {
        await mealAPI.create(mealData);
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!meal?._id || !window.confirm('Supprimer ce repas ?')) return;
    setLoading(true);
    try {
      await mealAPI.delete(meal._id);
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const showDetails = DETAIL_CATEGORIES.has(formData.category);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={meal ? 'Modifier le repas' : 'Ajouter un repas'}>

      {/* Date subtitle */}
      <p className="text-sm text-gray-400 capitalize -mt-1 mb-5">{formatDate(dateString)}</p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-danger rounded-lg text-danger text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── Catégorie ── */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Catégorie</div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {MEAL_CATEGORIES.map(cat => {
              const isSelected = formData.category === cat.name;
              const style = CATEGORY_STYLE[cat.name] ?? { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242' };
              return (
                <button
                  key={cat.name}
                  type="button"
                  disabled={loading}
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.name as Meal['category'] }))}
                  className="flex flex-col items-center justify-center rounded-xl py-3 px-1 border-2 transition-all duration-150 disabled:opacity-50 hover:opacity-90"
                  style={
                    isSelected
                      ? { backgroundColor: style.bg, borderColor: style.border, color: style.text }
                      : { backgroundColor: '#fafafa', borderColor: '#e5e7eb', color: '#9ca3af' }
                  }
                >
                  <span className="text-2xl leading-none">{cat.emoji}</span>
                  <span className="text-[10px] font-semibold mt-1.5 text-center leading-tight px-0.5">
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Personnes ── */}
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">
            Personnes
            {(formData.people.length + formData.extraPeople) > 0 && (
              <span className="ml-1.5 text-xs font-normal text-gray-400">
                {formData.people.length + formData.extraPeople} personne{(formData.people.length + formData.extraPeople) > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Avatar pills — horizontally scrollable, overflow contained */}
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {FAMILY_MEMBERS.map(person => {
                  const isSelected = formData.people.includes(person.name);
                  return (
                    <button
                      key={person.name}
                      type="button"
                      disabled={loading}
                      onClick={() => togglePerson(person.name)}
                      className="flex-shrink-0 flex flex-col items-center gap-1 px-3 py-2 rounded-xl border-2 transition-all duration-150 disabled:opacity-50"
                      style={
                        isSelected
                          ? { backgroundColor: person.color, borderColor: person.color, color: 'white' }
                          : { backgroundColor: '#fafafa', borderColor: '#e5e7eb', color: '#6b7280' }
                      }
                    >
                      <span className="text-xl leading-none">{person.emoji}</span>
                      <span className="text-xs font-semibold">{person.name}</span>
                    </button>
                  );
                })}
                <span className="flex-shrink-0 w-6" aria-hidden="true" />
              </div>
            </div>
            <div className="absolute right-0 top-0 bottom-1 w-10 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none flex items-center justify-end text-gray-400 pr-1">
              <ChevronRight />
            </div>
          </div>

          {/* Extra people — hidden by default */}
          {!showExtraPeople ? (
            <button
              type="button"
              onClick={() => setShowExtraPeople(true)}
              className="mt-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              + Invités supplémentaires
            </button>
          ) : (
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-gray-500">Invités supplémentaires</span>
              <input
                type="number"
                min={0}
                max={20}
                value={formData.extraPeople}
                onChange={e => setFormData(prev => ({
                  ...prev,
                  extraPeople: Math.max(0, parseInt(e.target.value) || 0),
                }))}
                disabled={loading}
                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => { setShowExtraPeople(false); setFormData(prev => ({ ...prev, extraPeople: 0 })); }}
                className="text-xs text-gray-400 hover:text-danger transition-colors"
              >
                Retirer
              </button>
            </div>
          )}
        </div>

        {/* ── Détails contextuels (Restaurant / Occasion spéciale uniquement) ── */}
        {showDetails && (
          <div className="space-y-3 pt-1 border-t border-gray-100">
            <Input
              label="Lieu"
              type="text"
              name="location"
              value={formData.location}
              onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
              disabled={loading}
              placeholder="Nom du restaurant, adresse, chez des amis..."
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={loading}
                placeholder="Réservation, menu, infos pratiques..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200"
              />
            </div>
          </div>
        )}

        {/* ── Nom (optionnel, en bas) ── */}
        <div className="border-t border-gray-100 pt-4">
          <Input
            label="Nom du repas (optionnel)"
            type="text"
            name="name"
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            disabled={loading}
            placeholder="Ex. Raclette, Pizza maison, Soupe..."
          />
          {!formData.name.trim() && (
            <p className="text-xs text-gray-400 mt-1">
              Sera enregistré comme « {formData.category} » si laissé vide
            </p>
          )}
        </div>

        {/* ── Actions ── */}
        <div className="flex items-center justify-between pt-1">
          {meal && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              aria-label="Supprimer"
              className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-danger/30 text-danger hover:bg-danger hover:text-white hover:border-danger transition-all duration-150 disabled:opacity-40"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/>
              </svg>
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Annuler"
              className="w-10 h-10 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all duration-150 disabled:opacity-40"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
            <button
              type="submit"
              disabled={loading}
              aria-label={meal ? 'Sauvegarder' : 'Ajouter'}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary text-white hover:bg-primary/90 transition-all duration-150 disabled:opacity-40"
            >
              {loading ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              )}
            </button>
          </div>
        </div>

      </form>
    </Modal>
  );
};

export default MealModal;
